import React, { useRef, useState } from "react";
import "./MediaDropZone.scss";

interface MediaDropZoneProps {
    accept: string;
    multiple?: boolean;
    hint: string;
    disabled?: boolean;
    onFilesSelected: (files: File[]) => void;
    className?: string;
    /** When provided, rendered inside the same drag&drop/click-to-browse target instead of the idle
     *  icon+hint placeholder — used to let an existing preview (image/video/slideshow item) still accept a
     *  drop to replace/add. */
    children?: React.ReactNode;
}

const ACCEPT_ANYTHING = "*/*";

/** Same meaning the browser gives `<input accept>`: a MIME type, a `type/*` wildcard or a `.extension`. A
 *  file the browser could not type (some video containers) is let through; the backend looks at it again. */
const matchesAccept = (file: File, accept: string): boolean => {
    if (!file.type) {
        return true;
    }
    return accept
        .split(",")
        .map((pattern) => pattern.trim().toLowerCase())
        .filter(Boolean)
        .some((pattern) => {
            if (pattern === ACCEPT_ANYTHING) {
                return true;
            }
            if (pattern.endsWith("/*")) {
                return file.type.toLowerCase().startsWith(pattern.slice(0, -1));
            }
            if (pattern.startsWith(".")) {
                return file.name.toLowerCase().endsWith(pattern);
            }
            return file.type.toLowerCase() === pattern;
        });
};

/** Reusable drag&drop + click-to-browse file picker. No existing pattern for this in the codebase — every
 *  other upload flow (MediaManager, WysiwygEditor) is click-only via a hidden input. */
const MediaDropZone: React.FC<MediaDropZoneProps> = ({ accept, multiple = false, hint, disabled = false, onFilesSelected, className, children }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);

    /** The file dialog applies `accept` and `multiple` by itself; a drop does not, so both are applied here. */
    const handleFiles = (fileList: FileList | null) => {
        const files = Array.from(fileList ?? []).filter((file) => matchesAccept(file, accept));
        if (files.length > 0) {
            onFilesSelected(multiple ? files : files.slice(0, 1));
        }
    };

    return (
        <div
            className={[
                "media-drop-zone",
                dragging && "media-drop-zone--dragging",
                disabled && "media-drop-zone--disabled",
                children && "media-drop-zone--filled",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
            onClick={() => !disabled && inputRef.current?.click()}
            onDragOver={(event) => {
                event.preventDefault();
                if (!disabled) {
                    setDragging(true);
                }
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                if (!disabled) {
                    handleFiles(event.dataTransfer.files);
                }
            }}
        >
            {children || (
                <>
                    <i className="pi pi-cloud-upload media-drop-zone__icon" />
                    <span className="media-drop-zone__hint">{hint}</span>
                </>
            )}
            <input
                ref={inputRef}
                type="file"
                hidden
                accept={accept}
                multiple={multiple}
                disabled={disabled}
                onChange={(event) => {
                    handleFiles(event.target.files);
                    event.target.value = "";
                }}
            />
        </div>
    );
};

export default MediaDropZone;
